import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  Send as SendIcon,
  EditNote as EditNoteIcon,
  SubdirectoryArrowRight as ReplyIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

interface ActionCardProps {
  id: string;
  title: string;
  type: "draft_email" | "write_value";
  buttonLabel: string;
  draftConfig?: {
    to: string[];
    subject: string;
    body: string;
    attachments?: string[];
  };
  checked: boolean;
  onCheckedChange: (id: string) => void;
  onExecute: () => void;
  hideButton?: boolean;
}

export const ActionCard: React.FC<ActionCardProps> = ({
  id,
  title,
  type,
  buttonLabel,
  draftConfig,
  checked,
  onCheckedChange,
  onExecute,
  hideButton,
}) => {
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const truncateBody = (body: string, maxLen = 100) => {
    if (body.length <= maxLen) return body;
    return body.slice(0, maxLen) + "...";
  };

  return (
    <>
      <Box
        sx={{
          bgcolor: "#E3F2FD",
          borderLeft: "3px solid #1565C0",
          borderRadius: 1,
          mb: 1,
          p: 1.5,
        }}
      >
        {/* Checkbox + title row */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Checkbox
            checked={checked}
            onChange={() => onCheckedChange(id)}
            size="small"
            sx={{ mt: -0.5, ml: -0.5, p: 0.5 }}
          />
          <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
            {title}
          </Typography>
        </Box>

        {type === "draft_email" && draftConfig && (
          <Box sx={{ ml: 4.5 }}>
            {/* To recipients */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
              <ReplyIcon sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                to: {draftConfig.to.join(", ")}
              </Typography>
            </Box>

            {/* Subject */}
            <Typography
              variant="caption"
              sx={{
                color: "#1565C0",
                textDecoration: "underline",
                display: "block",
                mb: 0.25,
              }}
            >
              {draftConfig.subject}
            </Typography>

            {/* Body preview */}
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, flexWrap: "wrap", mb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ fontStyle: "italic", color: "text.secondary" }}
              >
                {truncateBody(draftConfig.body)}
              </Typography>
              {draftConfig.body.length > 100 && (
                <Typography
                  variant="caption"
                  onClick={() => setEmailDialogOpen(true)}
                  sx={{
                    color: "#1565C0",
                    textDecoration: "underline",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    "&:hover": { opacity: 0.8 },
                  }}
                >
                  See full email
                </Typography>
              )}
            </Box>

            {/* Attachments + button row */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              {draftConfig.attachments && draftConfig.attachments.length > 0 ? (
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {draftConfig.attachments.map((attachment, i) => (
                    <Chip
                      key={i}
                      label={attachment}
                      icon={<AttachFileIcon />}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "0.65rem",
                        color: "text.secondary",
                        "& .MuiChip-icon": { fontSize: 14 },
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Box />
              )}

              {!hideButton && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SendIcon sx={{ fontSize: "14px !important" }} />}
                  onClick={onExecute}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    height: 28,
                    whiteSpace: "nowrap",
                    bgcolor: "#1565C0",
                    flexShrink: 0,
                    "&:hover": { bgcolor: "#0D47A1" },
                  }}
                >
                  {buttonLabel}
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* write_value: button on its own row */}
        {type === "write_value" && !hideButton && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<EditNoteIcon sx={{ fontSize: "14px !important" }} />}
              onClick={onExecute}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                height: 28,
                whiteSpace: "nowrap",
                bgcolor: "#1565C0",
                flexShrink: 0,
                "&:hover": { bgcolor: "#0D47A1" },
              }}
            >
              {buttonLabel}
            </Button>
          </Box>
        )}
      </Box>

      {/* Full email dialog */}
      {type === "draft_email" && draftConfig && (
        <Dialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Email Preview
            </Typography>
            <IconButton size="small" onClick={() => setEmailDialogOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
              To: {draftConfig.to.join(", ")}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
              {draftConfig.subject}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {draftConfig.body}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailDialogOpen(false)} size="small">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
